import React, { forwardRef, ReactNode, useId, useRef, useState, WheelEventHandler } from 'react';
import { FieldError } from 'react-hook-form';
import { ExclamationCircleIcon, EyeIcon, EyeSlashIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

import { classNames } from 'support/helpers/generic/generic';
import { FieldState } from 'components/Form/types';

import { InputLabel } from '../InputLabel/InputLabel';
import { LeadingAddOn } from '../LeadingAddOn/LeadingAddOn';
import { TrailingAddOn } from '../TrailingAddOn/TrailingAddOn';

import './Input.css';

import { useResizeObserver } from 'usehooks-ts';

import { Button } from 'components/Form/Button';

type NativeInputProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  | 'autoComplete'
  | 'className'
  | 'defaultValue'
  | 'disabled'
  | 'id'
  | 'max'
  | 'min'
  | 'name'
  | 'onClick'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'placeholder'
  | 'readOnly'
  | 'required'
  | 'step'
  | 'type'
  | 'minLength'
  | 'maxLength'
  | 'onKeyDown'
  | 'title'
>;

type InputVariantProps =
  | { variant?: 'default'; valueDescription?: never }
  | { variant: 'large'; valueDescription?: string };

type InputProps = InputVariantProps & {
  description?: ReactNode;
  errors?: FieldError;
  fieldState?: FieldState;
  label?: string;
  leftAddon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightAddon?: React.ReactNode;
  value?: React.InputHTMLAttributes<HTMLInputElement>['value'] | null;
  inputWrapperClassName?: string;
  wrapperClassName?: string;
  wrapperRef?: React.Ref<HTMLDivElement>;
} & NativeInputProps;

function InputComponent(
  {
    variant,
    valueDescription,
    label,
    leftIcon,
    leftAddon,
    rightAddon,
    className,
    inputWrapperClassName,
    wrapperClassName,
    errors,
    fieldState,
    disabled,
    required,
    description,
    type = 'text',
    id,
    wrapperRef,
    ...otherProps
  }: InputProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const inputId = useId();
  const { isPasswordVisible, toggleIsPasswordVisible } = usePasswordVisibility();
  const passwordVisiblityButton =
    type === 'password' ? (
      <Button
        variant="minimal"
        size="extra-small"
        iconOnly
        onClick={toggleIsPasswordVisible}
        LeftIcon={isPasswordVisible ? EyeIcon : EyeSlashIcon}
        analyticsId={''}
      />
    ) : null;
  let inputValue: InputProps['value'] = '';

  if (typeof otherProps.value === 'number') {
    if (!isNaN(otherProps.value)) {
      inputValue = otherProps.value;
    }
  } else if (otherProps.value !== null) {
    inputValue = otherProps.value;
  }

  const resolvedState: FieldState = fieldState ?? (errors ? 'error' : 'default');

  const leftAddonRef = useRef<HTMLDivElement>(null);
  const { width: leftAddonWidth } = useResizeObserver({
    ref: leftAddonRef,
    box: 'border-box',
  });

  const rightAddonRef = useRef<HTMLDivElement>(null);
  const { width: rightAddonWidth } = useResizeObserver({
    ref: rightAddonRef,
    box: 'border-box',
  });

  const isLarge = variant === 'large';

  return (
    <div
      className={classNames(wrapperClassName, {
        'text-red-700 focus-within:text-inherit': resolvedState === 'error',
        'text-blue-700 focus-within:text-inherit': resolvedState === 'pending-validation',
      })}
      ref={wrapperRef}
    >
      {/* Label + Description container when large */}
      {(label || description) && isLarge ? (
        <div className="mb-2 flex flex-col gap-1">
          <InputLabel label={label} variant={variant} required={required} />
          {description && <p className="text-sm text-gray-700">{description}</p>}
        </div>
      ) : (
        <InputLabel label={label} variant={variant} required={required} htmlFor={id ?? inputId} />
      )}

      {/* Value Description + Input container */}
      {isLarge && valueDescription && <p className="mb-1 text-sm text-gray-500">{valueDescription}</p>}

      <div className={classNames('group relative', !isLarge && label ? 'mt-1' : undefined, inputWrapperClassName)}>
        <LeadingAddOn show={!!leftIcon} leadingAddOn={leftIcon} state={resolvedState} />
        <LeadingAddOn show={!!leftAddon} leadingAddOn={leftAddon} state={resolvedState} ref={leftAddonRef} />
        <input
          ref={ref}
          type={type === 'password' && isPasswordVisible ? 'text' : type}
          className={classNames(
            'text-sm block w-full rounded-md focus-visible:outline-none box-border shadow-sm read-only:cursor-not-allowed disabled:cursor-not-allowed',
            'border-gray-300',
            {
              'pl-9': !leftAddon && leftIcon,
              'bg-red-50 placeholder:text-red-700 focus-visible:ring-red-500 focus-visible:border-red-500 focus-visible:outline-none focus:bg-transparent':
                resolvedState === 'error',
              'bg-blue-50 placeholder:text-blue-700 focus-visible:ring-blue-500 focus-visible:border-blue-500 focus-visible:outline-none focus:bg-transparent':
                resolvedState === 'pending-validation',
              'focus-visible:ring-procuros-green-400 focus-visible:border-procuros-green-400':
                resolvedState === 'default',
              'input--date': type === 'date',
              'data-hj-allow': type !== 'password',
              'disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 overflow-hidden flex items-center relative flex-1 min-w-0 bg-white border shadow-sm text-left focus-visible:outline-none active:outline-none active:ring-0 focus-visible:ring-0':
                disabled,
            },
            className,
          )}
          style={{
            paddingLeft: leftAddonWidth ? `calc(${leftAddonWidth}px + 0.5rem)` : undefined,
            paddingRight: type !== 'checkbox' && rightAddonWidth ? `calc(${rightAddonWidth}px + 0.5rem)` : undefined,
          }}
          aria-label={label ? undefined : otherProps.name}
          disabled={disabled}
          data-testid={id}
          id={id ?? inputId}
          {...otherProps}
          value={inputValue}
          onWheel={numberInputOnWheelPreventChange}
        />
        <TrailingAddOn
          state={resolvedState}
          ref={rightAddonRef}
          show={resolvedState !== 'default' || Boolean(rightAddon) || Boolean(passwordVisiblityButton)}
          disablePointerEvents={!passwordVisiblityButton}
          trailingAddOn={
            <div className="flex items-center gap-2">
              {rightAddon}
              {passwordVisiblityButton}
              {resolvedState === 'error' ? (
                <ExclamationCircleIcon className="size-5 text-red-500" aria-hidden="true" />
              ) : resolvedState === 'pending-validation' ? (
                <MinusCircleIcon className="size-5 text-blue-500" aria-hidden="true" />
              ) : null}
            </div>
          }
        />
      </div>

      {!!errors && <span className="mt-2 text-sm text-red-600">{errors.message}</span>}
      {!!description && !isLarge && <p className="mt-2 text-sm text-gray-500">{description}</p>}
    </div>
  );
}

export const Input = forwardRef(InputComponent);

const numberInputOnWheelPreventChange: WheelEventHandler<HTMLInputElement> = (e) => {
  const target = e.target as HTMLInputElement;
  // Prevent the input value change
  target.blur();

  // Prevent the page/container scrolling
  e.stopPropagation();
};

const usePasswordVisibility = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const toggleIsPasswordVisible = () => {
    setIsPasswordVisible((visible) => !visible);
  };

  return { isPasswordVisible, toggleIsPasswordVisible };
};
